import { memo, useEffect, useRef } from "react";
import { Card, Typography } from "antd";
import { motion } from "motion/react";
import type { SlideComponentProps } from "./SlideRenderer";
import { StreamingText } from "./StreamingText";
import { ANIMATION } from "./animationConfig";
import { useAnimationSequence } from "./useAnimationSequence";
import { FigurePlaceholders } from "types/";

export const FigureSlide = memo(
  ({ slide, isActive = false, onComplete }: SlideComponentProps) => {
    const placeholders = slide.placeholders as FigurePlaceholders;
    const slideTitle = placeholders.title;
    const figure = placeholders.figure;
    const hasCalledComplete = useRef(false);

    // Steps: 0=title, 1=figure, 2=description
    const totalSteps = 3;
    const { nextStep, isStepActive, isComplete } = useAnimationSequence(
      totalSteps,
      isActive
    );

    // Notify parent when complete (once)
    useEffect(() => {
      if (isComplete && onComplete && !hasCalledComplete.current) {
        hasCalledComplete.current = true;
        onComplete();
      }
    }, [isComplete, onComplete]);

    // Not active yet - render static content
    if (!isActive) {
      return (
        <Card>
          <Typography.Title level={4}>{slideTitle}</Typography.Title>
          <Typography.Title level={2} style={{ margin: 0 }}>
            {figure?.figure} <small>{figure?.unit}</small>
          </Typography.Title>
          <Typography.Text type="secondary">
            {figure?.short_description}
          </Typography.Text>
        </Card>
      );
    }

    return (
      <Card>
        {/* Step 0: Title */}
        {isStepActive(0) && (
          <Typography.Title level={4}>
            <StreamingText
              text={slideTitle}
              animate
              wordDelay={ANIMATION.WORD_DELAY}
              onComplete={nextStep}
            />
          </Typography.Title>
        )}

        {/* Step 1: Figure with scale animation */}
        {isStepActive(1) && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: ANIMATION.FIGURE_ANIMATION,
              ease: "easeOut",
            }}
            onAnimationComplete={nextStep}
          >
            <Typography.Title level={2} style={{ margin: 0 }}>
              {figure?.figure} <small>{figure?.unit}</small>
            </Typography.Title>
          </motion.div>
        )}

        {/* Step 2: Description */}
        {isStepActive(2) && (
          <Typography.Text type="secondary">
            <StreamingText
              text={figure?.short_description}
              animate
              wordDelay={ANIMATION.WORD_DELAY}
              onComplete={nextStep}
            />
          </Typography.Text>
        )}
      </Card>
    );
  }
);

FigureSlide.displayName = "FigureSlide";
